import { OppArrow } from './OppArrow.js';

/**
 * Renders one arrow per ply in the engine's predicted chain after the
 * player's intended move. Pairs with the ring highlights (Highlights.tsx
 * `ply-from`/`ply-to`): the rings mark the squares, these arrows tie
 * each ply's origin to its destination so the chain reads as connected
 * motion rather than disjoint squares.
 *
 * Color parity matches the ring palette:
 *   - Even plies (opponent moves) = cyan
 *   - Odd plies  (player counter-replies) = mint
 *
 * Deeper plies sit slightly higher (yOffset step) so they don't z-fight
 * with shallower plies or the last-move arrow. The animation staggers
 * by STAGGER_MS per ply so the chain plays out one move at a time.
 */

/** Hex colour for opponent-ply arrows (matches Highlights.tsx `OPP_PLY_COLOR`). */
const OPP_COLOR = '#7dd3fc';
/** Hex colour for player-ply arrows (matches Highlights.tsx `PLAYER_PLY_COLOR`). */
const PLAYER_COLOR = '#a7f3d0';
/** Vertical offset per ply (in board units). Ply 0 = ARROW_Y base. */
const PLY_Y_STEP = 0.012;
/** Animation stagger per ply in ms. 0 ply fires immediately; ply N fires
 *  after N * STAGGER_MS. */
const STAGGER_MS = 110;

export function PlyArrows({
  chain,
}: {
  readonly chain: ReadonlyArray<{ from: string; to: string }>;
}) {
  if (chain.length === 0) return null;
  return (
    <>
      {chain.map((ply, i) => {
        const isOpponent = i % 2 === 0;
        const color = isOpponent ? OPP_COLOR : PLAYER_COLOR;
        return (
          <OppArrow
            // Key includes `i` so a chain re-prediction that reorders a
            // ply doesn't preserve the wrong arrow's animation clock.
            key={`ply-${i}-${ply.from}-${ply.to}`}
            from={ply.from}
            to={ply.to}
            color={color}
            delayMs={i * STAGGER_MS}
            yOffset={i * PLY_Y_STEP}
          />
        );
      })}
    </>
  );
}
